/* Movatic WMS — Coletor | useTaskExecution (sequencia guiada, ADR-0034 F4b-6-i) | CesarFilho 932048016
   ----------------------------------------------------------------------------
   Extrai a logica de sequencia que vivia no CatalogViewer: le o next_step (o motor decide o
   passo), expoe o passo corrente + a linha corrente (com pick_mode/product_image do F4b-6-0), e
   dispacha as actions REAIS por passo (suporte->bind_support, endereco->start, produto->confirm_pick).
   Cada action, ao ter sucesso, RE-BUSCA o next_step -> avanca sozinho para o passo seguinte. Nao
   toca no motor (so' o consome). Partilhavel (coletor real hoje; o DEV mantem o seu proprio fluxo). */
import { useCallback, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import {
  bindSupport as apiBindSupport,
  confirmPick as apiConfirmPick,
  deliverTask as apiDeliver,
  getNextStep,
  startTask,
  type NextStepDTO,
} from '@/api/workforce'

/** UUID por gesto (idem_key, ADR-0033 D6): secure-context -> randomUUID; fallback http/IP. */
export function execUuid(): string {
  const c = (globalThis as { crypto?: { randomUUID?: () => string } }).crypto
  if (c?.randomUUID) return c.randomUUID()
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`
}

export type ConfirmPickArgs = {
  reservationId: number
  quantity?: number
  scanCode?: string
  idemKey?: string
}

export type TaskExecution = {
  nextStep: NextStepDTO | undefined
  loading: boolean
  busy: boolean
  error: string | null
  clearError: () => void
  /** dispacha a action do passo; devolve o resultado cru (confirm_pick) ou true/false. */
  bindSupport: (code: string) => Promise<boolean>
  startLocation: (scan: string) => Promise<boolean>
  confirmPick: (args: ConfirmPickArgs) => Promise<any | null>
  deliver: (scan: string) => Promise<boolean>
  refetch: () => void
}

export function useTaskExecution(taskId: number | null): TaskExecution {
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const q = useQuery({
    queryKey: ['task-execution', taskId],
    enabled: taskId != null,
    queryFn: () => getNextStep(taskId as number),
    refetchOnWindowFocus: false,
  })

  const msg = (e: any, def: string) => e?.response?.data?.detail || def

  const bindSupport = useCallback(
    async (code: string) => {
      if (taskId == null) return false
      setBusy(true)
      setError(null)
      try {
        await apiBindSupport(taskId, code)
        await q.refetch()
        return true
      } catch (e: any) {
        setError(msg(e, 'Falha ao vincular o suporte.'))
        return false
      } finally {
        setBusy(false)
      }
    },
    [taskId, q],
  )

  const startLocation = useCallback(
    async (scan: string) => {
      if (taskId == null) return false
      setBusy(true)
      setError(null)
      try {
        await startTask(taskId, scan)
        await q.refetch()
        return true
      } catch (e: any) {
        setError(msg(e, 'Falha ao iniciar no endereco.'))
        return false
      } finally {
        setBusy(false)
      }
    },
    [taskId, q],
  )

  const deliver = useCallback(
    async (scan: string) => {
      if (taskId == null) return false
      setBusy(true)
      setError(null)
      try {
        await apiDeliver(taskId, scan)
        await q.refetch() // avanca -> current_step='done'
        return true
      } catch (e: any) {
        setError(msg(e, 'Falha ao entregar o suporte.'))
        return false
      } finally {
        setBusy(false)
      }
    },
    [taskId, q],
  )

  const confirmPick = useCallback(
    async (args: ConfirmPickArgs) => {
      if (taskId == null) return null
      setBusy(true)
      setError(null)
      try {
        const r = await apiConfirmPick(taskId, {
          reservation_id: args.reservationId,
          qty: args.quantity,
          scan: args.scanCode,
          idem_key: args.idemKey,
        })
        await q.refetch()
        return r
      } catch (e: any) {
        setError(msg(e, 'Falha ao confirmar. Repita a leitura.'))
        return null
      } finally {
        setBusy(false)
      }
    },
    [taskId, q],
  )

  return {
    nextStep: q.data,
    loading: q.isLoading,
    busy,
    error,
    clearError: () => setError(null),
    bindSupport,
    startLocation,
    confirmPick,
    deliver,
    refetch: () => {
      void q.refetch()
    },
  }
}
